import React, { useCallback, useState, useEffect } from 'react';
import { Input, FormLabel } from '@getro/rombo';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import Range from '../../atoms/Range';

const RangeLabel = styled.span`
  flex: 0 0 auto;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.subtle};
`;

const InputsContainer = styled.div`
  display: flex;
  gap: 8px;

  p {
    color: ${({ theme }) => theme.colors.text.subtle};
    font-size: 14px;
    line-height: 1.4;
  }

  & > div {
    display: flex;
    gap: 8px;
    align-items: center;
  }
`;

const MultiRangeFilter = ({ field, form, title, min, max, ...props }) => {
  const value = field?.value || null;
  const [rangeValues, setRangeValues] = useState(value ? { min: value.min, max: value.max } : { min, max });

  // Update local state when field value changes
  useEffect(() => {
    if (value) {
      setRangeValues({ min: value.min, max: value.max });
    } else {
      setRangeValues({ min, max });
    }
  }, [value, min, max]);

  const onChange = (event, reverse = false) => {
    if (!event.target.value || Number.isNaN(Number(event.target.value))) {
      // eslint-disable-next-line no-param-reassign
      event.target.value = reverse ? min : max;
    }

    setRangeValues({
      min: reverse ? +event.target.value : rangeValues.min,
      max: !reverse ? +event.target.value : rangeValues.max,
    });
  };

  const onKeyDown = (event, reverse = false) => {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
    let currentValue = 0;

    if (event.key === 'ArrowUp') {
      currentValue += 1;
    }

    if (event.key === 'ArrowDown') {
      currentValue -= 1;
    }

    const newValue = reverse
      ? Math.min(rangeValues.min + currentValue, rangeValues.max - 1)
      : Math.max(rangeValues.max + currentValue, rangeValues.min + 1);

    setRangeValues({
      min: reverse ? Math.max(newValue, 0) : rangeValues.min,
      max: !reverse ? Math.min(newValue, max) : rangeValues.max,
    });
  };

  // Update local state immediately for responsive UI
  const handleRangeChange = useCallback((newRangeValues) => {
    setRangeValues(newRangeValues);
  }, []);

  // Apply changes to form with explicit values (avoids stale state reads)
  const applyChangesWithValues = useCallback(
    (values) => {
      // Apply changes using passed values instead of rangeValues state
      if (values.min === min && values.max === max) {
        if (form && field) {
          form.setFieldValue(field.name, null);
        }
      } else if (values.min < min || values.max < min || values.max < values.min || values.max > max) {
        if (form && field) {
          form.setFieldValue(field.name, null);
        }
      } else if (form && field) {
        form.setFieldValue(field.name, values);
      }
    },
    [min, max, form, field],
  );

  // Apply changes to form (used on blur or after drag)
  const applyChanges = useCallback(() => {
    applyChangesWithValues(rangeValues);
  }, [rangeValues, applyChangesWithValues]);

  return (
    <div
      style={{
        gap: '8px',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        flexDirection: 'column',
        position: 'relative',
      }}
      {...props}
    >
      <FormLabel>{title}</FormLabel>
      <div
        style={{
          gap: '8px',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 1,
        }}
      >
        <InputsContainer>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Input
              data-testid="min-range-number"
              size="small"
              placeholder={`${min}`}
              onKeyDown={(event) => onKeyDown(event, true)}
              onChange={(event) => onChange(event, true)}
              value={rangeValues.min === min ? '' : rangeValues.min}
              onBlur={() => {
                // Compute corrected values first
                const correctedMin = rangeValues.min < min || rangeValues.min > rangeValues.max ? min : rangeValues.min;
                const corrected = { min: correctedMin, max: rangeValues.max };
                setRangeValues(corrected);
                // Apply changes with corrected values to avoid stale state
                applyChangesWithValues(corrected);
              }}
            />
            <RangeLabel>to</RangeLabel>
            <Input
              size="small"
              placeholder={`${max - 1}+`}
              onKeyDown={(event) => onKeyDown(event, false)}
              onChange={(event) => onChange(event, false)}
              value={rangeValues.max === max ? '' : rangeValues.max}
              onBlur={() => {
                // Compute corrected values first
                const correctedMax =
                  rangeValues.max < min || rangeValues.max < rangeValues.min || rangeValues.max > max
                    ? max
                    : rangeValues.max;
                const corrected = { min: rangeValues.min, max: correctedMax };
                setRangeValues(corrected);
                // Apply changes with corrected values to avoid stale state
                applyChangesWithValues(corrected);
              }}
            />
          </div>
        </InputsContainer>
        <div role="presentation" onMouseUp={applyChanges} onTouchEnd={applyChanges}>
          <Range
            max={max}
            min={min}
            rangeValues={{
              max: Math.min(rangeValues.max, max),
              min: Math.min(rangeValues.min, max),
            }}
            onRangeChange={handleRangeChange}
          />
        </div>
      </div>
    </div>
  );
};

MultiRangeFilter.propTypes = {
  field: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.shape({
      min: PropTypes.number,
      max: PropTypes.number,
    }),
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
  }).isRequired,
  form: PropTypes.shape({
    setFieldValue: PropTypes.func.isRequired,
    setFieldTouched: PropTypes.func,
  }).isRequired,
  title: PropTypes.string,
  min: PropTypes.number,
  max: PropTypes.number,
};

MultiRangeFilter.defaultProps = {
  title: 'Years of experience',
  min: 0,
  max: 30,
};

export default MultiRangeFilter;
